// Centralized country list for consistent use across the application
// This ensures checkout and admin forms use the same country options

export interface Country {
  code: string;
  name: string;
  postalCodePattern?: RegExp;
  postalCodeFormat?: string;
  hasStates?: boolean;
  states?: { code: string; name: string }[];
}

// Countries with specific postal-code / state rules
const DETAILED_COUNTRIES: Country[] = [
  {
    code: 'US',
    name: 'United States',
    postalCodePattern: /^\d{5}(-\d{4})?$/,
    postalCodeFormat: '12345 or 12345-6789',
    hasStates: true,
    states: [
      { code: 'AL', name: 'Alabama' },
      { code: 'AK', name: 'Alaska' },
      { code: 'AZ', name: 'Arizona' },
      { code: 'AR', name: 'Arkansas' },
      { code: 'CA', name: 'California' },
      { code: 'CO', name: 'Colorado' },
      { code: 'CT', name: 'Connecticut' },
      { code: 'DE', name: 'Delaware' },
      { code: 'FL', name: 'Florida' },
      { code: 'GA', name: 'Georgia' },
      { code: 'HI', name: 'Hawaii' },
      { code: 'ID', name: 'Idaho' },
      { code: 'IL', name: 'Illinois' },
      { code: 'IN', name: 'Indiana' },
      { code: 'IA', name: 'Iowa' },
      { code: 'KS', name: 'Kansas' },
      { code: 'KY', name: 'Kentucky' },
      { code: 'LA', name: 'Louisiana' },
      { code: 'ME', name: 'Maine' },
      { code: 'MD', name: 'Maryland' },
      { code: 'MA', name: 'Massachusetts' },
      { code: 'MI', name: 'Michigan' },
      { code: 'MN', name: 'Minnesota' },
      { code: 'MS', name: 'Mississippi' },
      { code: 'MO', name: 'Missouri' },
      { code: 'MT', name: 'Montana' },
      { code: 'NE', name: 'Nebraska' },
      { code: 'NV', name: 'Nevada' },
      { code: 'NH', name: 'New Hampshire' },
      { code: 'NJ', name: 'New Jersey' },
      { code: 'NM', name: 'New Mexico' },
      { code: 'NY', name: 'New York' },
      { code: 'NC', name: 'North Carolina' },
      { code: 'ND', name: 'North Dakota' },
      { code: 'OH', name: 'Ohio' },
      { code: 'OK', name: 'Oklahoma' },
      { code: 'OR', name: 'Oregon' },
      { code: 'PA', name: 'Pennsylvania' },
      { code: 'RI', name: 'Rhode Island' },
      { code: 'SC', name: 'South Carolina' },
      { code: 'SD', name: 'South Dakota' },
      { code: 'TN', name: 'Tennessee' },
      { code: 'TX', name: 'Texas' },
      { code: 'UT', name: 'Utah' },
      { code: 'VT', name: 'Vermont' },
      { code: 'VA', name: 'Virginia' },
      { code: 'WA', name: 'Washington' },
      { code: 'WV', name: 'West Virginia' },
      { code: 'WI', name: 'Wisconsin' },
      { code: 'WY', name: 'Wyoming' }
    ]
  },
  {
    code: 'SA',
    name: 'Saudi Arabia',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    hasStates: false
  },
  {
    code: 'AE',
    name: 'United Arab Emirates',
    postalCodePattern: /^.{2,10}$/,
    postalCodeFormat: 'varies',
    hasStates: false
  },
  {
    code: 'GB',
    name: 'United Kingdom',
    postalCodePattern: /^[A-Z]{1,2}\d[A-Z\d]? \d[A-Z]{2}$/i,
    postalCodeFormat: 'SW1A 1AA',
    hasStates: false
  },
  {
    code: 'CA',
    name: 'Canada',
    postalCodePattern: /^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/,
    postalCodeFormat: 'A1A 1A1',
    hasStates: true,
    states: [
      { code: 'AB', name: 'Alberta' },
      { code: 'BC', name: 'British Columbia' },
      { code: 'MB', name: 'Manitoba' },
      { code: 'NB', name: 'New Brunswick' },
      { code: 'NL', name: 'Newfoundland and Labrador' },
      { code: 'NS', name: 'Nova Scotia' },
      { code: 'ON', name: 'Ontario' },
      { code: 'PE', name: 'Prince Edward Island' },
      { code: 'QC', name: 'Quebec' },
      { code: 'SK', name: 'Saskatchewan' },
      { code: 'NT', name: 'Northwest Territories' },
      { code: 'NU', name: 'Nunavut' },
      { code: 'YT', name: 'Yukon' }
    ]
  },
  {
    code: 'AU',
    name: 'Australia',
    postalCodePattern: /^\d{4}$/,
    postalCodeFormat: '1234',
    hasStates: true,
    states: [
      { code: 'NSW', name: 'New South Wales' },
      { code: 'VIC', name: 'Victoria' },
      { code: 'QLD', name: 'Queensland' },
      { code: 'WA', name: 'Western Australia' },
      { code: 'SA', name: 'South Australia' },
      { code: 'TAS', name: 'Tasmania' },
      { code: 'ACT', name: 'Australian Capital Territory' },
      { code: 'NT', name: 'Northern Territory' }
    ]
  },
  {
    code: 'IN',
    name: 'India',
    postalCodePattern: /^\d{6}$/,
    postalCodeFormat: '123456',
    hasStates: false
  },
  {
    code: 'DE',
    name: 'Germany',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    hasStates: false
  },
  {
    code: 'FR',
    name: 'France',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    hasStates: false
  },
  {
    code: 'IT',
    name: 'Italy',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    hasStates: false
  },
  {
    code: 'ES',
    name: 'Spain',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    hasStates: false
  },
  {
    code: 'JP',
    name: 'Japan',
    postalCodePattern: /^\d{3}-\d{4}$/,
    postalCodeFormat: '123-4567',
    hasStates: false
  },
  {
    code: 'BR',
    name: 'Brazil',
    postalCodePattern: /^\d{5}-?\d{3}$/,
    postalCodeFormat: '12345-678',
    hasStates: false
  },
  {
    code: 'MX',
    name: 'Mexico',
    postalCodePattern: /^\d{5}$/,
    postalCodeFormat: '12345',
    hasStates: false
  }
];

// Every other ISO 3166-1 country/territory, so checkout never needs an
// "Other" option (Gelato orders require a real 2-letter ISO code).
// Excluded: BLOCKED_COUNTRIES in utils/internationalization/locale.ts
// (KP, IR, SY, CU, SD, RU, BY) and uninhabited territories with no postal
// delivery (AQ, BV, GS, HM, IO, TF, UM).
const ISO_COUNTRIES: Array<[code: string, name: string]> = [
  ['AD', 'Andorra'],
  ['AE', 'United Arab Emirates'],
  ['AF', 'Afghanistan'],
  ['AG', 'Antigua & Barbuda'],
  ['AI', 'Anguilla'],
  ['AL', 'Albania'],
  ['AM', 'Armenia'],
  ['AO', 'Angola'],
  ['AR', 'Argentina'],
  ['AS', 'American Samoa'],
  ['AT', 'Austria'],
  ['AU', 'Australia'],
  ['AW', 'Aruba'],
  ['AX', 'Åland Islands'],
  ['AZ', 'Azerbaijan'],
  ['BA', 'Bosnia & Herzegovina'],
  ['BB', 'Barbados'],
  ['BD', 'Bangladesh'],
  ['BE', 'Belgium'],
  ['BF', 'Burkina Faso'],
  ['BG', 'Bulgaria'],
  ['BH', 'Bahrain'],
  ['BI', 'Burundi'],
  ['BJ', 'Benin'],
  ['BL', 'St. Barthélemy'],
  ['BM', 'Bermuda'],
  ['BN', 'Brunei'],
  ['BO', 'Bolivia'],
  ['BQ', 'Caribbean Netherlands'],
  ['BR', 'Brazil'],
  ['BS', 'Bahamas'],
  ['BT', 'Bhutan'],
  ['BW', 'Botswana'],
  ['BZ', 'Belize'],
  ['CA', 'Canada'],
  ['CC', 'Cocos (Keeling) Islands'],
  ['CD', 'Congo - Kinshasa'],
  ['CF', 'Central African Republic'],
  ['CG', 'Congo - Brazzaville'],
  ['CH', 'Switzerland'],
  ['CI', 'Côte d’Ivoire'],
  ['CK', 'Cook Islands'],
  ['CL', 'Chile'],
  ['CM', 'Cameroon'],
  ['CN', 'China'],
  ['CO', 'Colombia'],
  ['CR', 'Costa Rica'],
  ['CV', 'Cape Verde'],
  ['CW', 'Curaçao'],
  ['CX', 'Christmas Island'],
  ['CY', 'Cyprus'],
  ['CZ', 'Czechia'],
  ['DE', 'Germany'],
  ['DJ', 'Djibouti'],
  ['DK', 'Denmark'],
  ['DM', 'Dominica'],
  ['DO', 'Dominican Republic'],
  ['DZ', 'Algeria'],
  ['EC', 'Ecuador'],
  ['EE', 'Estonia'],
  ['EG', 'Egypt'],
  ['EH', 'Western Sahara'],
  ['ER', 'Eritrea'],
  ['ES', 'Spain'],
  ['ET', 'Ethiopia'],
  ['FI', 'Finland'],
  ['FJ', 'Fiji'],
  ['FK', 'Falkland Islands'],
  ['FM', 'Micronesia'],
  ['FO', 'Faroe Islands'],
  ['FR', 'France'],
  ['GA', 'Gabon'],
  ['GB', 'United Kingdom'],
  ['GD', 'Grenada'],
  ['GE', 'Georgia'],
  ['GF', 'French Guiana'],
  ['GG', 'Guernsey'],
  ['GH', 'Ghana'],
  ['GI', 'Gibraltar'],
  ['GL', 'Greenland'],
  ['GM', 'Gambia'],
  ['GN', 'Guinea'],
  ['GP', 'Guadeloupe'],
  ['GQ', 'Equatorial Guinea'],
  ['GR', 'Greece'],
  ['GT', 'Guatemala'],
  ['GU', 'Guam'],
  ['GW', 'Guinea-Bissau'],
  ['GY', 'Guyana'],
  ['HK', 'Hong Kong'],
  ['HN', 'Honduras'],
  ['HR', 'Croatia'],
  ['HT', 'Haiti'],
  ['HU', 'Hungary'],
  ['ID', 'Indonesia'],
  ['IE', 'Ireland'],
  ['IL', 'Israel'],
  ['IM', 'Isle of Man'],
  ['IN', 'India'],
  ['IQ', 'Iraq'],
  ['IS', 'Iceland'],
  ['IT', 'Italy'],
  ['JE', 'Jersey'],
  ['JM', 'Jamaica'],
  ['JO', 'Jordan'],
  ['JP', 'Japan'],
  ['KE', 'Kenya'],
  ['KG', 'Kyrgyzstan'],
  ['KH', 'Cambodia'],
  ['KI', 'Kiribati'],
  ['KM', 'Comoros'],
  ['KN', 'St. Kitts & Nevis'],
  ['KR', 'South Korea'],
  ['KW', 'Kuwait'],
  ['KY', 'Cayman Islands'],
  ['KZ', 'Kazakhstan'],
  ['LA', 'Laos'],
  ['LB', 'Lebanon'],
  ['LC', 'St. Lucia'],
  ['LI', 'Liechtenstein'],
  ['LK', 'Sri Lanka'],
  ['LR', 'Liberia'],
  ['LS', 'Lesotho'],
  ['LT', 'Lithuania'],
  ['LU', 'Luxembourg'],
  ['LV', 'Latvia'],
  ['LY', 'Libya'],
  ['MA', 'Morocco'],
  ['MC', 'Monaco'],
  ['MD', 'Moldova'],
  ['ME', 'Montenegro'],
  ['MF', 'St. Martin'],
  ['MG', 'Madagascar'],
  ['MH', 'Marshall Islands'],
  ['MK', 'North Macedonia'],
  ['ML', 'Mali'],
  ['MM', 'Myanmar (Burma)'],
  ['MN', 'Mongolia'],
  ['MO', 'Macao'],
  ['MP', 'Northern Mariana Islands'],
  ['MQ', 'Martinique'],
  ['MR', 'Mauritania'],
  ['MS', 'Montserrat'],
  ['MT', 'Malta'],
  ['MU', 'Mauritius'],
  ['MV', 'Maldives'],
  ['MW', 'Malawi'],
  ['MX', 'Mexico'],
  ['MY', 'Malaysia'],
  ['MZ', 'Mozambique'],
  ['NA', 'Namibia'],
  ['NC', 'New Caledonia'],
  ['NE', 'Niger'],
  ['NF', 'Norfolk Island'],
  ['NG', 'Nigeria'],
  ['NI', 'Nicaragua'],
  ['NL', 'Netherlands'],
  ['NO', 'Norway'],
  ['NP', 'Nepal'],
  ['NR', 'Nauru'],
  ['NU', 'Niue'],
  ['NZ', 'New Zealand'],
  ['OM', 'Oman'],
  ['PA', 'Panama'],
  ['PE', 'Peru'],
  ['PF', 'French Polynesia'],
  ['PG', 'Papua New Guinea'],
  ['PH', 'Philippines'],
  ['PK', 'Pakistan'],
  ['PL', 'Poland'],
  ['PM', 'St. Pierre & Miquelon'],
  ['PN', 'Pitcairn Islands'],
  ['PR', 'Puerto Rico'],
  ['PS', 'Palestinian Territories'],
  ['PT', 'Portugal'],
  ['PW', 'Palau'],
  ['PY', 'Paraguay'],
  ['QA', 'Qatar'],
  ['RE', 'Réunion'],
  ['RO', 'Romania'],
  ['RS', 'Serbia'],
  ['RW', 'Rwanda'],
  ['SA', 'Saudi Arabia'],
  ['SB', 'Solomon Islands'],
  ['SC', 'Seychelles'],
  ['SE', 'Sweden'],
  ['SG', 'Singapore'],
  ['SH', 'St. Helena'],
  ['SI', 'Slovenia'],
  ['SJ', 'Svalbard & Jan Mayen'],
  ['SK', 'Slovakia'],
  ['SL', 'Sierra Leone'],
  ['SM', 'San Marino'],
  ['SN', 'Senegal'],
  ['SO', 'Somalia'],
  ['SR', 'Suriname'],
  ['SS', 'South Sudan'],
  ['ST', 'São Tomé & Príncipe'],
  ['SV', 'El Salvador'],
  ['SX', 'Sint Maarten'],
  ['SZ', 'Eswatini'],
  ['TC', 'Turks & Caicos Islands'],
  ['TD', 'Chad'],
  ['TG', 'Togo'],
  ['TH', 'Thailand'],
  ['TJ', 'Tajikistan'],
  ['TK', 'Tokelau'],
  ['TL', 'Timor-Leste'],
  ['TM', 'Turkmenistan'],
  ['TN', 'Tunisia'],
  ['TO', 'Tonga'],
  ['TR', 'Türkiye'],
  ['TT', 'Trinidad & Tobago'],
  ['TV', 'Tuvalu'],
  ['TW', 'Taiwan'],
  ['TZ', 'Tanzania'],
  ['UA', 'Ukraine'],
  ['UG', 'Uganda'],
  ['US', 'United States'],
  ['UY', 'Uruguay'],
  ['UZ', 'Uzbekistan'],
  ['VA', 'Vatican City'],
  ['VC', 'St. Vincent & Grenadines'],
  ['VE', 'Venezuela'],
  ['VG', 'British Virgin Islands'],
  ['VI', 'U.S. Virgin Islands'],
  ['VN', 'Vietnam'],
  ['VU', 'Vanuatu'],
  ['WF', 'Wallis & Futuna'],
  ['WS', 'Samoa'],
  ['YE', 'Yemen'],
  ['YT', 'Mayotte'],
  ['ZA', 'South Africa'],
  ['ZM', 'Zambia'],
  ['ZW', 'Zimbabwe'],
];

const DETAILED_CODES = new Set(DETAILED_COUNTRIES.map(c => c.code));

// Comprehensive country list with all supported countries
export const ALL_COUNTRIES: Country[] = [
  ...DETAILED_COUNTRIES,
  ...ISO_COUNTRIES
    .filter(([code]) => !DETAILED_CODES.has(code))
    .map(([code, name]) => ({ code, name, hasStates: false }))
];

// Priority countries shown first in dropdowns
export const PRIORITY_COUNTRIES = ['US', 'SA', 'AE', 'GB', 'CA', 'AU', 'IN'];

// Get sorted countries with priority countries first
export function getSortedCountries(): Country[] {
  const priorityCountries = PRIORITY_COUNTRIES.map(code => 
    ALL_COUNTRIES.find(c => c.code === code)
  ).filter(Boolean) as Country[];
  
  const remainingCountries = ALL_COUNTRIES.filter(
    country => !PRIORITY_COUNTRIES.includes(country.code)
  ).sort((a, b) => a.name.localeCompare(b.name));
  
  return [...priorityCountries, ...remainingCountries];
}

// Get country data by code
export function getCountryByCode(code: string): Country | undefined {
  return ALL_COUNTRIES.find(country => country.code === code);
}

// Get states for a country
export function getStatesForCountry(countryCode: string): { code: string; name: string }[] {
  const country = getCountryByCode(countryCode);
  return country?.states || [];
}

// For backward compatibility with existing address validation
export const COUNTRIES = ALL_COUNTRIES.map(country => ({
  code: country.code,
  name: country.name,
  postalCodePattern: country.postalCodePattern || /^.{2,10}$/,
  postalCodeFormat: country.postalCodeFormat || 'varies',
  hasStates: country.hasStates || false,
  states: country.states
}));
